import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";

import { useAssetLookup } from "../../api/useAssetLookup";
import { FindingFixes } from "../../findings/FindingFixes";
import { formatList } from "../../format/formatList";
import { formatPickRate } from "../../format/formatPickRate";
import { formatSouls } from "../../format/formatSouls";
import type { Finding } from "../../model/Finding";
import { findingsOf } from "../../model/findingsOf";
import { lateDelayRange } from "../../model/lateDelayRange";

const TAKEAWAY = msg({
  id: "analysis.buy.takeaway",
  message:
    "{count, plural, one {Main thing: {items}, a key item on {hero}, was bought {delay} later than usual.} other {Main thing: {items}, key items on {hero}, were bought {delay} later than usual.}}",
});
const TAKEAWAY_SOULS = msg({
  id: "analysis.buy.takeaway_souls",
  message:
    "{count, plural, one {Main thing: {items}, a key item on {hero}, was bought later than {share} of strong players — at {souls} souls, half of them buy it by {baseline}.} other {Main thing: {items}, key items on {hero}, were bought later than almost all strong players.}}",
});
const DELAY_RANGE = msg({ id: "analysis.buy.delay_range", message: "{from}–{to} min" });
const DELAY_ONE = msg({ id: "analysis.buy.delay_one", message: "{minutes} min" });
const CAUSE_OTHER_ITEMS = msg({
  id: "analysis.buy.cause.other_items_first",
  message:
    "Why: before {item}, {souls, plural, one {# soul} other {# souls}} went to items rarely bought on this hero or bought later — {items}.",
});
const CAUSE_NO_SOULS = msg({
  id: "analysis.buy.cause.not_enough_souls",
  message: "Why: there were not enough souls for {item} — farm was behind.",
});

type BuyTakeawayProps = {
  findings: readonly Finding[];
  heroId: number;
};

// Главный вывод «Закупа» по находкам `item_bought_late`: какие ключевые предметы и насколько
// опоздали и почему — по первой поздней покупке (дальше опоздания тянутся из неё).
// Поздних предметов нет — блока нет.
export const BuyTakeaway = ({ findings, heroId }: BuyTakeawayProps) => {
  const { _, i18n } = useLingui();
  const findItem = useAssetLookup("items");
  const hero = useAssetLookup("heroes")(heroId);
  const late = findingsOf(findings, "item_bought_late");
  const delay = lateDelayRange(findings);
  if (delay === null) {
    return null;
  }
  const first = late.reduce((a, b) => (b.params.bought_at_s < a.params.bought_at_s ? b : a));
  const items = formatList(
    late.map((finding) => findItem(finding.params.item_id).name),
    i18n.locale,
  );
  const { souls, baseline_souls: baselineSouls, later_than: laterThan } = first.params;
  const firstItem = findItem(first.params.item_id).name;
  const cause =
    first.params.cause === "other_items_first"
      ? _({
          ...CAUSE_OTHER_ITEMS,
          values: {
            item: firstItem,
            souls: first.params.spent_elsewhere,
            items: formatList(
              first.params.items_before.map((id) => findItem(id).name),
              i18n.locale,
            ),
          },
        })
      : first.params.cause === "not_enough_souls"
        ? _({ ...CAUSE_NO_SOULS, values: { item: firstItem } })
        : null;
  return (
    <div className="flex flex-col gap-1.5 rounded-[10px] bg-surface-raised px-4 py-3.5 text-sm text-foreground-soft">
      <p>
        {souls != null && baselineSouls != null && laterThan != null
          ? _({
              ...TAKEAWAY_SOULS,
              values: {
                count: late.length,
                items,
                hero: hero.name,
                souls: formatSouls(souls, i18n.locale),
                baseline: formatSouls(baselineSouls, i18n.locale),
                share: formatPickRate(laterThan, i18n.locale),
              },
            })
          : _({
              ...TAKEAWAY,
              values: {
                count: late.length,
                items,
                hero: hero.name,
                delay:
                  delay.from === delay.to
                    ? _({ ...DELAY_ONE, values: { minutes: delay.from } })
                    : _({ ...DELAY_RANGE, values: delay }),
              },
            })}
      </p>
      {cause !== null && <p>{cause}</p>}
      <FindingFixes finding={first} className="mt-1 flex flex-col gap-1" />
    </div>
  );
};
